import {
  CaretRightOutlined,
  ExportOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Col,
  Collapse,
  Dropdown,
  Input,
  Row,
  Table,
  Tag,
  theme,
} from 'antd';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useGetMpCatalogues } from '../../../../../utils/api/mpCatalogue/useGetMpCatalogue';
import {
  getStatusColor,
  getStatusLabel,
} from '../../../../../utils/fn/mpProducts';
import slugify from 'slugify';

const Associations = ({ productDetails }) => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const productId = useMemo(() => productDetails.id, [productDetails]);
  const [searchText, setSearchText] = useState('');

  const { mpCatalogues, isLoading } = useGetMpCatalogues({
    product: productId,
  });

  const panelStyle = {
    marginBottom: 24,
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    border: 'none',
  };

  // Grouper les produits par marketplace
  const groupedByMarketplace = useMemo(() => {
    if (!mpCatalogues || !Array.isArray(mpCatalogues)) {
      // console.log('No mpCatalogues or not an array:', mpCatalogues);
      return {};
    }

    const grouped = {};
    mpCatalogues.forEach((item) => {
      const marketplaceName = item.mp_category.marketplace.name;
      if (!grouped[marketplaceName]) {
        grouped[marketplaceName] = {
          marketplace: item.mp_category.marketplace,
          products: [],
        };
      }
      grouped[marketplaceName].products.push(item);
    });
    // console.log('Grouped by marketplace:', grouped);
    return grouped;
  }, [mpCatalogues]);

  const columns = [
    {
      title: 'Store',
      dataIndex: 'storeName',
      key: 'store',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
      ),
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const menuItems = [
          {
            key: 'view-product',
            label: 'Voir le produit',
            icon: <EyeOutlined />,
            onClick: () => {
              navigate(`/dashboard/mp-products/${record.mpProductId}`);
            },
          },
          {
            key: 'view-category',
            label: 'Voir la catégorie',
            icon: <ExportOutlined />,
            onClick: () => {
              const recordSlug = slugify(record.category || '', {
                lower: true,
                strict: true,
              });
              navigate(
                `/dashboard/marketplaces/${record.marketplaceName}/categories/${recordSlug}/category-attributes`
              );
            },
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="default"
              size="small"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  // TODO : Unifier les logos (en utilisant la constante MARKETPLACE_ICONS)
  const getMarketplaceLogo = (marketplaceName) => {
    const logos = {
      leroymerlin: '/icons/logo-leroymerlin.svg',
      casanoov: '/icons/logo-casanoov.svg',
      cazeboo: '/icons/logo-cazeboo.svg',
      sicaan: '/icons/logo-sicaan.svg',
    };
    return logos[marketplaceName] || null;
  };

  const getItems = (panelStyle) => {
    return Object.entries(filteredMarketplaces).map(
      ([marketplaceName, data]) => {
        const tableData = data.products.map((item, index) => ({
          key: item.id || index,
          mpProductId: item.id,
          storeName: item.store.name,
          country: item.store.country,
          status: item.status,
          category: item.mp_category.label,
          marketplaceName: marketplaceName,
        }));

        const logo = getMarketplaceLogo(marketplaceName);

        return {
          key: marketplaceName,
          label: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {logo && (
                <img
                  src={logo}
                  alt={data.marketplace.title}
                  style={{ height: '24px' }}
                />
              )}
              <h5 style={{ margin: 0 }}>
                {data.marketplace.title} ({data.products.length})
              </h5>
            </div>
          ),
          children: (
            <Table
              columns={columns}
              dataSource={tableData}
              pagination={false}
              loading={isLoading}
            />
          ),
          style: panelStyle,
        };
      }
    );
  };

  // Filtrer les marketplaces selon la recherche
  const filteredMarketplaces = useMemo(() => {
    if (!searchText.trim()) {
      return groupedByMarketplace;
    }

    const searchLower = searchText.toLowerCase();
    const filtered = {};

    Object.entries(groupedByMarketplace).forEach(([name, data]) => {
      const marketplaceTitle = data.marketplace.title.toLowerCase();
      const marketplaceName = name.toLowerCase();

      if (
        marketplaceTitle.includes(searchLower) ||
        marketplaceName.includes(searchLower)
      ) {
        filtered[name] = data;
      }
    });

    return filtered;
  }, [groupedByMarketplace, searchText]);

  const hasData = useMemo(
    () => Object.keys(filteredMarketplaces).length > 0,
    [filteredMarketplaces]
  );

  return (
    <div style={{ padding: '0 24px 24px 24px' }}>
      <h1>Associations Marketplaces</h1>
      <Row>
        <Col span={12}>
          <Input
            style={{ marginBottom: 16 }}
            prefix={<SearchOutlined />}
            placeholder="Rechercher une marketplace"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
        </Col>
        <Col span={24}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              Chargement...
            </div>
          ) : !hasData ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              {searchText
                ? `Aucune marketplace trouvée pour "${searchText}".`
                : 'Aucune association marketplace trouvée pour ce produit.'}
            </div>
          ) : (
            <Collapse
              bordered={false}
              expandIcon={({ isActive }) => (
                <CaretRightOutlined rotate={isActive ? 90 : 0} />
              )}
              style={{ background: token.colorBgContainer }}
              items={getItems(panelStyle)}
            />
          )}
        </Col>
      </Row>
    </div>
  );
};

export default Associations;
